import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const RegisterPage = () => {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(form.name, form.email, form.password);
      toast.success('Registration successful! / 註冊成功！');
      navigate('/app');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed / 註冊失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-fac-dark flex items-center justify-center px-4 pt-16">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo192.png" alt="FAC Solutions" className="w-16 h-16 object-contain" />
          <span className="font-heading font-bold text-3xl text-white">
            FAC<span className="text-fac-green">VALVE</span>
          </span>
        </Link>

        {/* Form Card */}
        <div className="bg-fac-card border border-border rounded-lg p-8 cyber-border" data-testid="register-form-card">
          <h1 className="font-heading font-bold text-3xl text-white uppercase text-center mb-2">
            {t('register_title')}
          </h1>
          <p className="text-fac-green font-chinese text-center mb-8">註冊</p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-white font-chinese">
                {t('register_name')} / 姓名
              </Label>
              <Input
                id="name"
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white h-12"
                placeholder="Your name / 您的姓名"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-white font-chinese">
                {t('login_email')} / 電子郵件
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white h-12"
                placeholder="your@email.com"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white font-chinese">
                {t('login_password')} / 密碼
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="bg-fac-dark border-border focus:border-fac-green text-white h-12 pr-12"
                  placeholder="••••••••"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1 font-chinese">
                Minimum 6 characters / 至少 6 個字元
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-skew bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim h-14"
              data-testid="register-submit-btn"
            >
              <span>{loading ? t('loading') : t('register_submit')}</span>
            </Button>
          </form>

          <p className="text-center text-muted-foreground mt-6 font-chinese">
            <Link to="/login" className="text-fac-green hover:underline" data-testid="login-link">
              {t('register_login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
